import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { Info as InfoIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Tooltip, Autocomplete, TextField } from '@mui/material';
import CustomButton from '../../../components/common/Button';
import { ShiftFormData } from '../types';
import { FETCH_SHIFT, UPDATE_SHIFT } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';
import { cityOptions } from '../../../constants/enums';

const EditShift: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: shiftData, loading: shiftLoading, error: shiftError } = useQuery(FETCH_SHIFT, {
    ...stagingOptions,
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const [updateShiftMutation] = useMutation(UPDATE_SHIFT, {
    ...stagingOptions,
  });

  const [formData, setFormData] = useState<ShiftFormData>({
    name: '',
    startTime: '',
    endTime: '',
    requiredDrivers: 1,
    maxDrivers: 1,
    cityId: '',
    neighborhoodId: '',
    zone: '',
    startLatitude: undefined,
    startLongitude: undefined,
  });

  useEffect(() => {
    if (shiftData?.fetchShift) {
      const apiShift = shiftData.fetchShift;

      if (apiShift.status?.toLowerCase() !== 'draft') {
        toast.error('Only draft shifts can be edited');
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate('/shift-management');
        }
        return;
      }

      setFormData({
        name: apiShift.name || '',
        startTime: apiShift.startTime ? new Date(apiShift.startTime).toISOString().slice(0, 16) : '',
        endTime: apiShift.endTime ? new Date(apiShift.endTime).toISOString().slice(0, 16) : '',
        requiredDrivers: apiShift.requiredDrivers || 1,
        maxDrivers: apiShift.requiredDrivers || 1,
        cityId: apiShift.city?.id || apiShift.cityId || '',
        neighborhoodId: apiShift.neighborhood?.id || '',
        zone: apiShift.neighborhood?.name || '',
        startLatitude: apiShift.startLatitude,
        startLongitude: apiShift.startLongitude,
      });
    } else if (shiftError && id) {
      toast.error('Shift not found');
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/shift-management');
      }
    }
  }, [shiftData, shiftError, id, navigate]);

  const handleInputChange = (field: keyof ShiftFormData, value: string | number | undefined) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Shift Name is required';
    }

    if (!formData.startTime) {
      errors.startTime = 'Start Time is required';
    }

    if (!formData.endTime) {
      errors.endTime = 'End Time is required';
    }

    const requiredDrivers = formData.requiredDrivers || formData.maxDrivers || 1;
    if (requiredDrivers < 1) {
      errors.requiredDrivers = 'Required Drivers must be at least 1';
    }

    if (formData.startTime) {
      const startDate = new Date(formData.startTime);
      if (isNaN(startDate.getTime())) {
        errors.startTime = 'Invalid Start Time format';
      }
    }

    if (formData.endTime) {
      const endDate = new Date(formData.endTime);
      if (isNaN(endDate.getTime())) {
        errors.endTime = 'Invalid End Time format';
      } else if (formData.startTime) {
        const startDate = new Date(formData.startTime);
        if (!isNaN(startDate.getTime()) && endDate <= startDate) {
          errors.endTime = 'End time must be after start time';
        }
      }
    }

    if (formData.startLatitude !== undefined) {
      if (formData.startLatitude < -90 || formData.startLatitude > 90) {
        errors.startLatitude = 'Start Latitude must be between -90 and 90';
      }
    }

    if (formData.startLongitude !== undefined) {
      if (formData.startLongitude < -180 || formData.startLongitude > 180) {
        errors.startLongitude = 'Start Longitude must be between -180 and 180';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setFieldErrors({});

    try {
      setSaving(true);

      const input: any = {
        id: id!,
        name: formData.name.trim(),
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        requiredDrivers: formData.requiredDrivers || formData.maxDrivers || 1,
      };

      if (formData.cityId && formData.cityId.trim() !== '') {
        input.cityId = parseInt(formData.cityId);
      }
      if (formData.neighborhoodId && formData.neighborhoodId.trim() !== '') {
        input.neighborhoodId = parseInt(formData.neighborhoodId);
      }
      if (formData.startLatitude !== undefined && formData.startLatitude !== null) {
        input.startLatitude = formData.startLatitude;
      }
      if (formData.startLongitude !== undefined && formData.startLongitude !== null) {
        input.startLongitude = formData.startLongitude;
      }

      await updateShiftMutation({
        variables: { input }
      });

      toast.success('Shift updated successfully');
      navigate('/shift-management', { state: { refresh: true } });
    } catch (error: any) {
      console.error('Failed to update shift', error);
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to update shift. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/shift-management');
  };

  if (shiftLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryColor mx-auto mb-4"></div>
          <p className="text-gray-600">Loading shift data...</p>
        </div>
      </div>
    );
  }

  if (!shiftData?.fetchShift && !shiftLoading) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-2 bg-white">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8">

          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '20px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="mb-8">
            <div className="bg-gradient-to-r from-primaryColor via-primaryColor to-darkBlue rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
              <div className="relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="text-3xl font-bold text-white">Edit Shift Session</h1>
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30">
                        Draft
                      </span>
                    </div>
                    <p className="text-base text-white/90 leading-relaxed max-w-2xl">
                      Update shift schedule details. Only draft shifts can be edited. Once activated, shifts cannot be modified.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shift Session Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    placeholder="Enter shift session name"
                    className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {fieldErrors.name && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.startTime}
                      onChange={(e) => handleInputChange('startTime', e.target.value)}
                      required
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.startTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.startTime && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.startTime}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.endTime}
                      onChange={(e) => handleInputChange('endTime', e.target.value)}
                      required
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.endTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.endTime && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.endTime}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required Drivers <span className="text-red-500">*</span>
                      <Tooltip title="Maximum number of drivers allowed for this shift (default: 1). This determines the slot limit for driver assignments." arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={formData.requiredDrivers || formData.maxDrivers || 1}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        handleInputChange('requiredDrivers', value);
                        handleInputChange('maxDrivers', value);
                      }}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.requiredDrivers ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.requiredDrivers ? (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.requiredDrivers}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">Default: 1 driver</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <Autocomplete
                      options={cityOptions}
                      getOptionLabel={(option) => option.name || ''}
                      value={cityOptions.find(city => String(city.id) === String(formData.cityId)) || null}
                      onChange={(event, newValue) => {
                        const value = newValue ? String(newValue.id) : '';
                        handleInputChange('cityId', value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select a city"
                          required
                          error={!!fieldErrors.cityId}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '2px 4px',
                              fontSize: '14px',
                              '& fieldset': {
                                borderColor: fieldErrors.cityId ? '#F44336' : '#D1D5DB',
                              },
                              '&:hover fieldset': {
                                borderColor: fieldErrors.cityId ? '#F44336' : '#D1D5DB',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: fieldErrors.cityId ? '#F44336' : '#303F9F',
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              padding: '8px 12px',
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                          {option.name}
                        </li>
                      )}
                      sx={{
                        '& .MuiAutocomplete-inputRoot': {
                          padding: '0 !important',
                        },
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: '300px',
                        },
                      }}
                    />
                    {fieldErrors.cityId && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.cityId}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neighborhood / Working Zone
                    <Tooltip title="Optional: Select a specific city/neighborhood" arrow placement="top">
                      <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                    </Tooltip>
                  </label>
                  <Autocomplete
                    options={cityOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={cityOptions.find(city => String(city.id) === String(formData.neighborhoodId || formData.zone)) || null}
                    onChange={(event, newValue) => {
                      const value = newValue ? String(newValue.id) : '';
                      handleInputChange('neighborhoodId', value);
                      handleInputChange('zone', value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select a city/neighborhood (optional)"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            padding: '2px 4px',
                            fontSize: '14px',
                            '& fieldset': {
                              borderColor: '#D1D5DB',
                            },
                            '&:hover fieldset': {
                              borderColor: '#D1D5DB',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#303F9F',
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            padding: '8px 12px',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                        {option.name}
                      </li>
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        padding: '0 !important',
                      },
                    }}
                    ListboxProps={{
                      style: {
                        maxHeight: '300px',
                      },
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Latitude
                      <Tooltip title="Optional: Starting point latitude for geofencing and zone boundary alerts" arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="-90"
                      max="90"
                      value={formData.startLatitude || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        handleInputChange('startLatitude', value);
                      }}
                      placeholder="24.7136"
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.startLatitude ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.startLatitude && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.startLatitude}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Longitude
                      <Tooltip title="Optional: Starting point longitude for geofencing and zone boundary alerts" arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="-180"
                      max="180"
                      value={formData.startLongitude || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        handleInputChange('startLongitude', value);
                      }}
                      placeholder="46.6753"
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.startLongitude ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.startLongitude && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.startLongitude}</p>
                    )}
                  </div>
                </div>
                {(shiftData?.fetchShift?.createdAt || shiftData?.fetchShift?.updatedAt) && (
                  <div className="grid grid-cols-2 gap-4">
                    {shiftData?.fetchShift?.createdAt && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                          Created At
                        </label>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(shiftData.fetchShift.createdAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                      </div>
                    )}
                    {shiftData?.fetchShift?.updatedAt && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                          Updated At
                        </label>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(shiftData.fetchShift.updatedAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <InfoIcon sx={{ fontSize: '18px', color: '#3B82F6', marginTop: '2px' }} />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Note:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Only draft shifts can be edited</li>
                        <li>Changes will not affect already assigned drivers</li>
                        <li>Make sure required drivers count is sufficient for current assignments</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <CustomButton
                  variant="outlined"
                  color="primary"
                  onClick={handleCancel}
                  additionalStyles="min-w-[120px]"
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                  additionalStyles="min-w-[120px] shadow-md hover:shadow-lg transition-shadow"
                >
                  {saving ? 'Updating...' : 'Update Shift'}
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditShift;

